/**
 * Lightweight event bus for cross-layer communication
 * (e.g., between Liquid templates and Angular controllers).
 *
 * Based on appExtensionSdk/utils/eventBus.js pattern (Map + Set),
 * with added `once` support and error boundary.
 */
import slEvents from '../constants/slEventBusEventNames';

function createEventBus() {
  const eventMap = new Map();

  /**
   * Register a listener for an event.
   * @param {string} eventName
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  function on(eventName, callback) {
    if (!callback) throw new Error(`Missing callback for event: ${eventName}`);

    if (!eventMap.has(eventName)) {
      eventMap.set(eventName, new Set());
    }

    eventMap.get(eventName).add(callback);

    // support anonymous callback to unsubscription
    return () => off(eventName, callback);
  }

  /**
   * Register a one-time listener that auto-removes after first invocation.
   * @param {string} eventName
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  function once(eventName, callback) {
    if (!callback) throw new Error(`Missing callback for event: ${eventName}`);

    const wrapper = (...payload) => {
      off(eventName, wrapper);
      callback(...payload);
    };

    wrapper.originalCallback = callback;

    on(eventName, wrapper);

    return () => off(eventName, wrapper);
  }

  /**
   * Remove a specific listener for an event.
   * @param {string} eventName
   * @param {Function} callback
   */
  function off(eventName, callback) {
    if (!callback) throw new Error(`Missing callback for event: ${eventName}`);

    if (!eventMap.has(eventName)) return;

    const listeners = eventMap.get(eventName);

    // Support comparison of regular callbacks and wrappers created by once
    for (let listener of listeners) {
      if (listener === callback || listener.originalCallback === callback) {
        listeners.delete(listener);
        break;
      }
    }

    if (listeners.size === 0) {
      eventMap.delete(eventName);
    }
  }

  /**
   * Emit an event, calling all registered listeners with the provided payload.
   * @param {string} eventName
   * @param {...*} payload
   */
  function emit(eventName, ...payload) {
    const listeners = eventMap.get(eventName);
    if (!listeners || listeners.size === 0) return;

    const listenersCopy = Array.from(listeners);

    listenersCopy.forEach((cb) => {
      try {
        cb(...payload);
      } catch (error) {
        console.error(
          `[EventBus Error] Exception caught in listener for "${eventName}":`,
          error,
        );
      }
    });
  }

  return { on, once, off, emit };
}

// Singleton: reuse if already created (e.g., by another bundle)
if (!window.slEventBus) {
  const bus = createEventBus();

  Object.defineProperty(window, 'slEventBus', {
    value: Object.freeze(bus),
    writable: false,
    configurable: false,
  });
}

export default window.slEventBus;
export { slEvents };
